import { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { FirmarkMark } from "./FirmarkMark";

type MegaLink = { to: string; label: string; desc: string };

// Platform mega-menu — deep pages + home-section anchors (hash links scroll via
// PublicLayout's useScrollToHash, working cross-page).
const PLATFORM_LINKS: MegaLink[] = [
  { to: "/platform/design-engine", label: "Design engine", desc: "Floor plan → a complete structural design" },
  { to: "/platform/engineering", label: "Engineering & codes", desc: "Calc engine, sourced material data, deliverables" },
  { to: "/security", label: "Security & data trust", desc: "Tenant-isolated, encrypted, audited" },
  { to: "/#how-it-works", label: "Transparency", desc: "See every step; defend every number" },
  { to: "/#control-modes", label: "Control modes", desc: "Manual, automated, or AI-supervised" },
  { to: "/#platform", label: "The platform tour", desc: "Sites, design, member checks, AI guardrails & org" },
];
const ROLE_LINKS: MegaLink[] = [
  { to: "/#for-engineers", label: "For engineers", desc: "Defend every decision you stamp" },
  { to: "/#for-builders", label: "For builders", desc: "Permit-ready plans, lot after lot" },
  { to: "/plans", label: "Send us a plan", desc: "Done-for-you: PDF/DWG in, stamp-ready set back" },
];

const TOP_LINKS = [
  { to: "/platform/design-engine", label: "Design engine" },
  { to: "/platform/engineering", label: "Engineering" },
  { to: "/security", label: "Security" },
  { to: "/learn", label: "Learn" },
  { to: "/help", label: "Help" },
  { to: "/plans", label: "Send a plan" },
  { to: "/#pricing", label: "Pricing" },
];

export function PublicHeader() {
  const { state } = useAuth();
  const authed = state.kind === "authenticated";
  const { pathname } = useLocation();
  // Deep-page current-page indicator (hash links + "/" never mark as current).
  const isCurrentPage = (to: string) => !to.includes("#") && to !== "/" && (pathname === to || pathname.startsWith(`${to}/`));
  const [platformOpen, setPlatformOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const platformRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const megaId = useId();
  const drawerId = useId();

  // Close the mega-menu / mobile drawer on Escape (returning focus to whichever
  // trigger opened it), and close the mega-menu on click-outside.
  useEffect(() => {
    if (!platformOpen && !mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (mobileOpen) { setMobileOpen(false); toggleRef.current?.focus(); }
      if (platformOpen) { setPlatformOpen(false); triggerRef.current?.focus(); }
    };
    const onClick = (e: MouseEvent) => {
      if (platformRef.current && !platformRef.current.contains(e.target as Node)) setPlatformOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); };
  }, [platformOpen, mobileOpen]);

  // Move focus into the drawer when it opens (keyboard users land on the nav).
  useEffect(() => {
    if (mobileOpen) drawerRef.current?.querySelector<HTMLAnchorElement>("a")?.focus();
  }, [mobileOpen]);

  // Hover intent (desktop): open immediately, close on a short delay so a diagonal
  // mouse move to the panel doesn't dismiss it.
  const openMega = () => { window.clearTimeout(closeTimer.current); setPlatformOpen(true); };
  const scheduleClose = () => { closeTimer.current = window.setTimeout(() => setPlatformOpen(false), 120); };

  const closeAll = () => { setPlatformOpen(false); setMobileOpen(false); };

  return (
    <header className="landing-header">
      <div className="landing-wrap landing-header-inner">
        <Link to="/" className="brand" aria-label="Firmark Engineering Studio — home" onClick={closeAll}>
          <FirmarkMark size={22} />
          <span className="brand-word">Firmark</span>
          <span className="brand-product">Engineering Studio</span>
        </Link>

        <nav className="landing-nav landing-nav--top" aria-label="Primary">
          <div className="topnav">
            {/* Platform mega-menu */}
            <div
              className="mega"
              ref={platformRef}
              onMouseEnter={openMega}
              onMouseLeave={scheduleClose}
            >
              <button
                ref={triggerRef}
                type="button"
                className={`topnav-link mega-trigger${platformOpen ? " is-open" : ""}`}
                aria-haspopup="true"
                aria-expanded={platformOpen}
                aria-controls={platformOpen ? megaId : undefined}
                onClick={() => setPlatformOpen((v) => !v)}
              >
                Platform
                <svg className="mega-caret" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 3.5 L5 6.5 L8 3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>

              {platformOpen && (
                <div className="mega-panel" id={megaId} aria-label="Platform" onMouseEnter={openMega} onMouseLeave={scheduleClose}>
                  <div className="mega-col">
                    <p className="mega-eyebrow">The platform</p>
                    {PLATFORM_LINKS.map((l) => (
                      <Link key={l.label} to={l.to} className="mega-item" aria-current={isCurrentPage(l.to) ? "page" : undefined} onClick={closeAll}>
                        <span className="mega-item-label">{l.label}</span>
                        <span className="mega-item-desc">{l.desc}</span>
                      </Link>
                    ))}
                  </div>
                  <div className="mega-col mega-col--side">
                    <p className="mega-eyebrow">By role</p>
                    {ROLE_LINKS.map((l) => (
                      <Link key={l.label} to={l.to} className="mega-item" onClick={closeAll}>
                        <span className="mega-item-label">{l.label}</span>
                        <span className="mega-item-desc">{l.desc}</span>
                      </Link>
                    ))}
                    <Link to="/learn" className="mega-cta" onClick={closeAll}>
                      Start here → Learn
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {TOP_LINKS.map((l) => (
              <Link key={l.label} to={l.to} className="topnav-link" aria-current={isCurrentPage(l.to) ? "page" : undefined} onClick={closeAll}>{l.label}</Link>
            ))}
          </div>

          <div className="landing-nav-cta">
            {authed ? (
              <Link to="/app" className="btn btn-primary" onClick={closeAll}>Open the app →</Link>
            ) : (
              <>
                <Link to="/login" className="btn btn-ghost" onClick={closeAll}>Sign in</Link>
                <Link to="/beta?intent=demo" className="btn btn-primary" onClick={closeAll}>Book a demo</Link>
              </>
            )}
          </div>

          {/* Mobile menu toggle */}
          <button
            ref={toggleRef}
            type="button"
            className="mnav-toggle"
            aria-expanded={mobileOpen}
            aria-controls={drawerId}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileOpen((v) => !v)}
          >
            <span className={`mnav-burger${mobileOpen ? " is-open" : ""}`} aria-hidden="true" />
          </button>
        </nav>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="mnav-drawer" id={drawerId} ref={drawerRef}>
          <div className="landing-wrap mnav-drawer-inner">
            <p className="mega-eyebrow">Platform</p>
            {PLATFORM_LINKS.map((l) => (
              <Link key={l.label} to={l.to} className="mnav-item" aria-current={isCurrentPage(l.to) ? "page" : undefined} onClick={closeAll}>{l.label}</Link>
            ))}
            <p className="mega-eyebrow">More</p>
            <Link to="/learn" className="mnav-item" onClick={closeAll}>Learn</Link>
            <Link to="/help" className="mnav-item" onClick={closeAll} aria-current={isCurrentPage("/help") ? "page" : undefined}>Help</Link>
            <Link to="/plans" className="mnav-item" onClick={closeAll} aria-current={isCurrentPage("/plans") ? "page" : undefined}>Send us a plan</Link>
            <Link to="/#pricing" className="mnav-item" onClick={closeAll}>Pricing</Link>
            <Link to="/#for-engineers" className="mnav-item" onClick={closeAll}>For engineers</Link>
            <Link to="/#for-builders" className="mnav-item" onClick={closeAll}>For builders</Link>
            <div className="mnav-cta">
              {authed ? (
                <Link to="/app" className="btn btn-primary" onClick={closeAll}>Open the app →</Link>
              ) : (
                <>
                  <Link to="/login" className="btn btn-secondary" onClick={closeAll}>Sign in</Link>
                  <Link to="/beta?intent=demo" className="btn btn-primary" onClick={closeAll}>Book a demo</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
